// Notification Sender Service
// Utility functions to send push notifications for various events

import { supabase } from './supabase';

export interface SendNotificationOptions {
  user_id?: string;
  user_ids?: string[];
  business_id: string;
  title: string;
  body: string;
  type: 'booking' | 'message' | 'task' | 'payment' | 'system';
  category?: string;
  url?: string;
  icon?: string;
  image?: string;
  booking_id?: string;
  customer_id?: string;
  task_id?: string;
  requireInteraction?: boolean;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
}

/**
 * Send a push notification via Supabase Edge Function
 */
export async function sendPushNotification(options: SendNotificationOptions): Promise<boolean> {
  try {
    console.log('🔔 [SENDER] Sending push notification:', options);

    const requestBody = {
      user_id: options.user_id,
      user_ids: options.user_ids,
      business_id: options.business_id,
      notification: {
        title: options.title,
        body: options.body,
        icon: options.icon || '/favicon.ico',
        url: options.url || '/',
        tag: `${options.type}-${Date.now()}`,
        requireInteraction: options.requireInteraction || false,
        actions: options.actions || [
          { action: 'view', title: 'View' },
          { action: 'dismiss', title: 'Dismiss' }
        ],
        data: {
          type: options.type,
          category: options.category,
          booking_id: options.booking_id,
          customer_id: options.customer_id,
          task_id: options.task_id
        }
      },
      notification_type: options.type,
      category: options.category,
      booking_id: options.booking_id,
      customer_id: options.customer_id,
      task_id: options.task_id
    };

    console.log('🔔 [SENDER] Request body:', requestBody);

    // Get the current session for auth
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      console.error('🔔 [SENDER] No active session found');
      return false;
    }

    // Call Edge Function directly with fetch
    try {
      const response = await fetch('https://idvgoctfaarxiryxprpy.supabase.co/functions/v1/send-push-notification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImlkdmdvY3RmYWFyeGlyeXhwcnB5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE2OTkyOTI0NzAsImV4cCI6MjAxNDg2ODQ3MH0.v5WTEDndZhNvZxnNMJwL5XF5VvW0RdLPJYg5pVBLKOo'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔔 [SENDER] Edge Function error:', response.status, errorText);
        return false;
      }

      const data = await response.json();
      console.log('🔔 [SENDER] Push notification response:', data);
      return data?.success || false;
    } catch (fetchError) {
      console.error('🔔 [SENDER] Fetch error:', fetchError);
      return false;
    }

  } catch (error) {
    console.error('🔔 [SENDER] Failed to send push notification:', error);
    console.error('🔔 [SENDER] Error stack:', error.stack);
    return false;
  }
}

// Predefined notification templates for common events

/**
 * Send notification for new booking
 */
export async function notifyNewBooking(
  businessId: string,
  bookingId: string,
  customerName: string,
  serviceDate: string,
  serviceName?: string
): Promise<boolean> {
  return sendPushNotification({
    business_id: businessId,
    title: 'New Booking Created',
    body: `New booking from ${customerName} for ${serviceDate}${serviceName ? ` - ${serviceName}` : ''}`,
    type: 'booking',
    category: 'new_booking',
    url: `/bookings?id=${bookingId}`,
    booking_id: bookingId,
    requireInteraction: true,
    actions: [
      { action: 'view', title: 'View Booking' },
      { action: 'dismiss', title: 'Dismiss' }
    ]
  });
}

/**
 * Send notification for booking changes
 */
export async function notifyBookingChange(
  businessId: string,
  bookingId: string,
  customerName: string,
  changeType: 'rescheduled' | 'modified' | 'cancelled',
  details?: string
): Promise<boolean> {
  const titles = {
    rescheduled: 'Booking Rescheduled',
    modified: 'Booking Modified',
    cancelled: 'Booking Cancelled'
  };

  const bodies = {
    rescheduled: `${customerName}'s booking has been rescheduled${details ? ` - ${details}` : ''}`,
    modified: `${customerName}'s booking has been modified${details ? ` - ${details}` : ''}`,
    cancelled: `${customerName}'s booking has been cancelled${details ? ` - ${details}` : ''}`
  };

  return sendPushNotification({
    business_id: businessId,
    title: titles[changeType],
    body: bodies[changeType],
    type: 'booking',
    category: `booking_${changeType}`,
    url: `/bookings?id=${bookingId}`,
    booking_id: bookingId,
    actions: [
      { action: 'view', title: 'View Details' },
      { action: 'dismiss', title: 'Dismiss' }
    ]
  });
}

/**
 * Send notification for new customer message
 */
export async function notifyNewMessage(
  businessId: string,
  customerId: string,
  customerName: string,
  messagePreview: string,
  channelType: 'sms' | 'email' | 'call'
): Promise<boolean> {
  const channelNames = {
    sms: 'SMS',
    email: 'Email',
    call: 'Call'
  };

  return sendPushNotification({
    business_id: businessId,
    title: `New ${channelNames[channelType]} from ${customerName}`,
    body: messagePreview.length > 100 ? `${messagePreview.substring(0, 100)}...` : messagePreview,
    type: 'message',
    category: 'new_message',
    url: `/messages?customer=${customerId}`,
    customer_id: customerId,
    requireInteraction: true,
    actions: [
      { action: 'view', title: 'View Message' },
      { action: 'dismiss', title: 'Dismiss' }
    ]
  });
}

/**
 * Send notification for task assignment
 */
export async function notifyTaskAssignment(
  businessId: string,
  userId: string,
  taskId: string,
  taskTitle: string,
  assignedBy: string,
  dueDate?: string
): Promise<boolean> {
  return sendPushNotification({
    user_id: userId,
    business_id: businessId,
    title: 'New Task Assigned',
    body: `"${taskTitle}" assigned by ${assignedBy}${dueDate ? ` - Due: ${dueDate}` : ''}`,
    type: 'task',
    category: 'task_assignment',
    url: `/tasks?id=${taskId}`,
    task_id: taskId,
    requireInteraction: true,
    actions: [
      { action: 'view', title: 'View Task' },
      { action: 'dismiss', title: 'Dismiss' }
    ]
  });
}

/**
 * Send notification for payment received
 */
export async function notifyPaymentReceived(
  businessId: string,
  customerId: string,
  customerName: string,
  amount: number,
  bookingId?: string
): Promise<boolean> {
  return sendPushNotification({
    business_id: businessId,
    title: 'Payment Received',
    body: `$${amount.toFixed(2)} payment received from ${customerName}`,
    type: 'payment',
    category: 'payment_received',
    url: bookingId ? `/bookings?id=${bookingId}` : `/customers?id=${customerId}`,
    customer_id: customerId,
    booking_id: bookingId,
    actions: [
      { action: 'view', title: 'View Details' },
      { action: 'dismiss', title: 'Dismiss' }
    ]
  });
}

/**
 * Send notification for workflow completion
 */
export async function notifyWorkflowCompletion(
  businessId: string,
  workflowName: string,
  bookingId: string,
  customerName: string,
  completedSteps: number,
  totalSteps: number
): Promise<boolean> {
  return sendPushNotification({
    business_id: businessId,
    title: 'Workflow Completed',
    body: `${workflowName} completed for ${customerName} (${completedSteps}/${totalSteps} steps)`,
    type: 'system',
    category: 'workflow_completion',
    url: `/bookings?id=${bookingId}`,
    booking_id: bookingId,
    actions: [
      { action: 'view', title: 'View Booking' },
      { action: 'dismiss', title: 'Dismiss' }
    ]
  });
}

/**
 * Send system alert notification
 */
export async function notifySystemAlert(
  businessId: string,
  title: string,
  message: string,
  alertType: 'info' | 'warning' | 'error' | 'success',
  url?: string
): Promise<boolean> {
  const icons = {
    info: '💡',
    warning: '⚠️',
    error: '❌',
    success: '✅'
  };

  return sendPushNotification({
    business_id: businessId,
    title: `${icons[alertType]} ${title}`,
    body: message,
    type: 'system',
    category: 'system_alert',
    url: url || '/',
    requireInteraction: alertType === 'error' || alertType === 'warning',
    actions: [
      { action: 'view', title: 'View' },
      { action: 'dismiss', title: 'Dismiss' }
    ]
  });
}

/**
 * Batch send notifications to multiple users
 */
export async function sendBatchNotifications(
  userIds: string[],
  options: Omit<SendNotificationOptions, 'user_id' | 'user_ids'>
): Promise<boolean> {
  if (userIds.length === 0) {
    console.warn('No user IDs provided for batch notification');
    return false;
  }

  return sendPushNotification({
    ...options,
    user_ids: userIds
  });
}

/**
 * Send notification to all business users
 */
export async function sendBusinessWideNotification(
  businessId: string,
  title: string,
  body: string,
  type: SendNotificationOptions['type'],
  options?: Partial<SendNotificationOptions>
): Promise<boolean> {
  return sendPushNotification({
    business_id: businessId,
    title,
    body,
    type,
    ...options
  });
}
